// @flow

import styled from '@emotion/styled'
import * as React from 'react'

import { colors, fontSizes, fonts } from '@toggl/style'
import { text } from '@toggl/ui'
import { inputCss } from '@toggl/ui/src/form/styles'

import { useScript } from '../hooks/useScript'

declare var hbspt: any

const HUBSPOT_REGION = 'na1'
const HUBSPOT_PORTAL_ID = '21421994'
const FORM_ELEMENT_ID = 'hubspot-integration-review-form'

export function HubSpotForm({
  formId,
  onFormReady,
  product = 'track',
}: {
  formId: string,
  // TODO: Properly type the hubSpot `formHandler`
  onFormReady?: (form: HTMLFormElement, formHandler?: any) => void,
  product?: 'track' | 'work',
}) {
  useScript(
    'https://js.hsforms.net/forms/embed/v3.js',
    React.useCallback(() => {
      const formHandler = hbspt?.forms.create({
        region: HUBSPOT_REGION,
        portalId: HUBSPOT_PORTAL_ID,
        formId,
        target: `#${FORM_ELEMENT_ID}`,
        onFormReady: (form) => onFormReady?.(form, formHandler),
      })
    }, [formId, onFormReady])
  )

  return <FormContainer product={product} id={FORM_ELEMENT_ID} />
}

const FormContainer: React.ComponentType<any> = styled.div`
  h1,
  h2,
  h3,
  h4,
  h5 {
    font-family: ${fonts.gtHaptikMedium};
    color: ${colors.darkPurple100};
  }
  h3 {
    margin-bottom: 10px;
  }
  input,
  select,
  textarea {
    ${inputCss};
    width: 100% !important;
    &.invalid,
    &.error {
      border-color: ${colors.red};
    }
  }
  label,
  input,
  select,
  textarea {
    color: ${colors.darkPurple100} !important;
    font-family: ${fonts.inter} !important;
  }
  select {
    -moz-appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    appearance: none;
    padding-right: 35px;
    background: transparent;
    background-image: url("data:image/svg+xml,%3Csvg width='17' height='16' viewBox='0 0 17 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M4.07707 6.79211C3.71659 6.43162 3.68886 5.86439 3.99388 5.4721L4.07707 5.37789C4.4676 4.98737 5.10076 4.98737 5.49129 5.37789L8.78418 8.67L12.0771 5.37789C12.4376 5.01741 13.0048 4.98968 13.3971 5.2947L13.4913 5.37789C13.8818 5.76842 13.8818 6.40158 13.4913 6.79211C11.3886 8.89533 10.1619 10.1221 9.49118 10.793C9.10071 11.1835 8.46816 11.184 8.07768 10.7935C7.40326 10.119 6.17167 8.8872 4.07707 6.79211Z' fill='%232C1338'/%3E%3C/svg%3E%0A");
    background-position-x: calc(100% - 8px);
    background-repeat: no-repeat;
    background-size: 22px 100%;
  }
  .hs-form__field-row {
    display: flex;
    gap: 15px;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .hs-form__field-row__column {
    flex: 1;
    padding: 0 !important;
  }
  .hs-form__row:not(:last-of-type) {
    margin-left: auto !important;
    margin-right: auto !important;
    margin-top: 10px !important;
    &:not(:last-of-type) {
      margin-bottom: 20px !important;
    }
  }
  .hs-form__field__input,
  .input {
    margin-right: 0 !important;
    width: 100% !important;
    &[type='tel'] {
      flex: 1;
    }
  }
  .hs-form__field__phone {
    display: flex;
    align-items: flex-start;
    gap: 15px;
    > select {
      flex: 0 0 120px;
    }
  }
  label:not(.hs-error-msg) {
    display: block;
    margin-bottom: 8px;
  }
  ul.hs-form__alerts__list {
    padding: 0;
    margin-top: 5px;
    margin-bottom: 0;
    list-style: none;
    li {
      font-size: 14px;
      color: ${colors.red};
    }
  }
  button,
  .hs-form__actions__submit {
    font-size: ${fontSizes.paragraph3} !important;
    font-family: ${fonts.gtHaptikMedium} !important;
    background: ${(props) =>
      props.product === 'work' ? colors.teal110 : colors.pink100} !important;
    color: ${colors.white};
    border-radius: 200px;
    cursor: pointer;
    border: none;
    height: 52px;
    display: inline-flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
    &:hover {
      background: ${colors.darkPurple90} !important;
    }
  }
  div[data-hsfc-id='RichText'] p {
    color: ${colors.darkPurple70};
    font-size: ${fontSizes.paragraph5};
    line-height: 1.5;
    a {
      text-decoration: underline;
      color: ${colors.darkPurple70};
      &:hover {
        color: ${colors.darkPurple100};
      }
    }
  }
  .hs_recaptcha,
  .hs_submit {
    max-width: 500px;
    margin-left: auto !important;
    margin-right: auto !important;
  }
  .hs-form__field__options__container {
    display: flex;
    flex-wrap: nowrap;
  }
  .hs-form__field__radio__label-text {
    display: block;
    text-align: center;
    width: 30px;
    cursor: pointer;
  }
  .hs-form__thankyou-message p {
    ${text.paragraph3};
    text-align: center;
    color: ${colors.darkPurple90};
  }
`
